import { apiServiceInstance } from "@/core/helpers/utils";
import type {
    ExcelExportResult,
    ThuHoiVatTuPickerItem,
    ThuHoiVtptDetailQuery,
    ThuHoiVtptDetailResponse,
    ThuHoiVtptQuery,
    ThuHoiVtptSummaryResponse,
} from "@/types/thu-hoi-vtpt";
import moment from "moment";
import {
    ApiResponse,
    ApiResponseWithExtra,
    ChartDataTypeResponseBase,
    IBangKeChiTietXuatResponseBase,
    IChiTietXuatTheoDoiTuongResponseBase,
    ICompanyReportPaginationMeta,
    IDonViPdfExtra,
    IGiaThanhSuaChuaCacCapResponseBase,
    IHoaDonNhapKhoResponseBase,
    INhapKhoVatTuPhuTungResponseBase,
    INhapXuatTonKhoResponseBase,
    ISoCanDoiVatTuExportResponseBase,
    ISoCanDoiVatTuExtraResponseBase,
    ITaiKhoanTheoHopDongResponseBase,
    ITheoDoiThucHienHopDongResponseBase,
    IToaXeCapSuaChuaReportResponseBase,
    ITongHopVatTuResponseBase,
    ITongHopVatTuTheoDoiTuongResponseBase,
    IVatTuHangTonKhoItemV2ResponseBase,
    IVatTuHangTonKhoResponseBase,
    PaginationResponseBase,
} from "../interfaces/ApiResponses";

export class BaoCaoService {
  static getSoCanDoiVatTu({
    from,
    to,
    khoId,
  }: {
    from: number;
    to: number;
    khoId: number;
  }): Promise<{
    data: ApiResponse<IVatTuHangTonKhoResponseBase[]>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/bao-cao/so-can-doi-vat-tu?query[id-kho]=${khoId}&query[from-month]=${from}&query[to-month]=${to}`
    );
  }

  static getSoCanDoiVatTuV2({
    from,
    to,
    khoId,
    page,
    perPage,
    search,
    hideZero,
  }: {
    from: number;
    to: number;
    khoId: number;
    page?: number;
    perPage?: number;
    search?: string;
    hideZero?: boolean;
  }): Promise<{
    data: ApiResponseWithExtra<
      PaginationResponseBase<IVatTuHangTonKhoItemV2ResponseBase>,
      ISoCanDoiVatTuExtraResponseBase
    >;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      "/api/v1/report/inventory/monthly-summary",
      {
        params: {
          id_kho: khoId,
          start_month: from,
          end_month: to,
          ...(page ? { page } : {}),
          ...(perPage ? { per_page: perPage } : {}),
          ...(search ? { search } : {}),
          ...(hideZero ? { hide_zero: 1 } : {}),
        },
        // Override the default `perpage` header (from user settings) so the
        // backend paginates by the requested page size instead of ignoring it.
        ...(perPage ? { headers: { perpage: perPage.toString() } } : {}),
      }
    );
  }

  static exportSoCanDoiVatTu({
    from,
    to,
    khoId,
    search,
    hideZero,
  }: {
    from: number;
    to: number;
    khoId: number;
    search?: string;
    hideZero?: boolean;
  }): Promise<{
    data: ApiResponse<ISoCanDoiVatTuExportResponseBase>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      "/api/v1/report/inventory/monthly-summary/export",
      {
        params: {
          id_kho: khoId,
          start_month: from,
          end_month: to,
          ...(search ? { search } : {}),
          ...(hideZero ? { hide_zero: 1 } : {}),
        },
      }
    );
  }

  static async getAllSoCanDoiVatTuV2({
    from,
    to,
    khoId,
    search,
    hideZero,
    perPage = 150,
    onProgress,
  }: {
    from: number;
    to: number;
    khoId: number;
    search?: string;
    hideZero?: boolean;
    perPage?: number;
    onProgress?: (loadedPage: number, totalPages: number) => void;
  }): Promise<{
    items: IVatTuHangTonKhoItemV2ResponseBase[];
    extra: ISoCanDoiVatTuExtraResponseBase | undefined;
  }> {
    const fetchPage = (page: number) =>
      BaoCaoService.getSoCanDoiVatTuV2({
        from,
        to,
        khoId,
        search,
        hideZero,
        page,
        perPage,
      });

    const firstResponse = await fetchPage(1);
    const { data: firstPage, extra } = firstResponse.data;
    const items = [...firstPage.data];
    const { last_page: lastPage } = firstPage;

    onProgress?.(1, lastPage);

    for (let page = 2; page <= lastPage; page++) {
      const response = await fetchPage(page);

      items.push(...response.data.data.data);
      onProgress?.(page, lastPage);
    }

    return { items, extra };
  }

  static getBangKeChiTietNhap({
    from,
    to,
    idKho,
    mact,
    matk,
  }: {
    from: number;
    to: number;
    idKho: number;
    mact: string;
    matk: string;
  }): Promise<{
    data: ApiResponse<IHoaDonNhapKhoResponseBase[]>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/bao-cao/bang-ke-chi-tiet-nhap?query[id-kho]=${idKho}&query[from-month]=${from}&query[to-month]=${to}&query[ma-tk]=${matk}&query[ma-ct]=${mact}`
    );
  }

  static getBangKeChiTietXuat({
    capSuaChuaId,
    khoId,
    from,
    to,
    toaXeId,
  }: {
    capSuaChuaId: number;
    khoId: number;
    toaXeId: number;
    from: number;
    to: number;
  }): Promise<{
    data: ApiResponse<IBangKeChiTietXuatResponseBase[]>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/bao-cao/bang-ke-chi-tiet-xuat?query[id-kho]=${khoId}&query[from-month]=${from}&query[to-month]=${to}&query[id-cap-sc]=${capSuaChuaId}&query[id-toa-xe]=${toaXeId}`
    );

    // For testing purposes, you can use the following commented out code:
    // return apiServiceInstance().vueInstance.axios.get(
    //   `/api/v1/bao-cao/bang-ke-chi-tiet-xuat?query[id-kho]=${1}&query[from-month]=${7}&query[to-month]=${12}&query[id-cap-sc]=${6}&query[id-toa-xe]=${2023}`,
    // );
  }

  static async getTongHopVatTu({
    khoId,
    isTongHopNhap,
    from,
    to,
  }: {
    khoId: number;
    isTongHopNhap: boolean;
    from: number;
    to: number;
  }): Promise<{
    items: ITongHopVatTuResponseBase[];
    extra?: IDonViPdfExtra;
  }> {
    const type = isTongHopNhap ? "tong-hop-nhap" : "tong-hop-xuat";

    const { data } = await apiServiceInstance().vueInstance.axios.get(
      `/api/v1/bao-cao/${type}?query[id-kho]=${khoId}&query[from-month]=${from}&query[to-month]=${to}`
    );

    return {
      items: data.data.map((item) => ({
        don_vi_tinh: item.don_vi_tinh,
        id: item.id,
        ma_vat_tu: item.ma_vat_tu,
        don_gia: +item.nhap_trong_ky_dg ?? +item.xuat_trong_ky_dg ?? 0,
        so_luong: +item.nhap_trong_ky_slg ?? +item.xuat_trong_ky_slg ?? 0,
        thanh_tien: +item.nhap_trong_ky_tt ?? +item.xuat_trong_ky_tt ?? 0,
        ten_vat_tu: item.ten_vat_tu,
      })),
      extra: data.extra,
    };
  }

  static getBangKeChiTietXuatTheoDoiTuong({
    capSuaChuaId,
    khoId,
    from,
    to,
    toaXeId,
  }: {
    capSuaChuaId: number;
    khoId: number;
    from: Date;
    to: Date;
    toaXeId: number;
  }): Promise<{
    data: ApiResponse<IChiTietXuatTheoDoiTuongResponseBase[]>;
  }> {
    const fromDate = moment(from).format("YYYY-MM-DD");

    const toDate = moment(to).format("YYYY-MM-DD");

    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/bao-cao/bang-ke-chi-tiet-xuat-theo-doi-tuong?query[id-kho]=${khoId}&query[start-date]=${fromDate}&query[end-date]=${toDate}&query[id-cap-sc]=${capSuaChuaId}&query[id-toa-xe]=${toaXeId}`
    );

    // For testing purposes, you can use the following commented out code:
    // return apiServiceInstance().vueInstance.axios.get(`/api/v1/bao-cao/bang-ke-chi-tiet-xuat-theo-doi-tuong?query[id-kho]=2&query[start-date]=2023-07-01&query[end-date]=2023-07-31&query[id-cap-sc]=5&query[id-toa-xe]=926`);
  }

  static getTheoDoiThucHienHopDong({
    idHopDong,
    idKho,
    month,
  }: {
    idHopDong: number;
    idKho: number;
    month: number;
  }): Promise<{
    data: ApiResponse<ITheoDoiThucHienHopDongResponseBase[]>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/bao-cao/theo-doi-thuc-hien-hop-dong?query[id-hop-dong]=${idHopDong}&query[id-kho]=${idKho}&query[month]=${month}`
    );

    // For testing purposes, you can use the following commented out code:
    // return apiServiceInstance().vueInstance.axios.get(
    //   `/api/v1/bao-cao/theo-doi-thuc-hien-hop-dong?query[id-hop-dong]=${149}&query[id-kho]=${3}&query[month]=${7}`,
    // );
  }

  static getTongHopVatTuTheoDoiTuong({
    kho_id,
    month,
  }: {
    kho_id: number;
    month: number;
  }): Promise<{
    data: ApiResponse<ITongHopVatTuTheoDoiTuongResponseBase[]>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/bao-cao/tong-hop-nhap-vat-tu-theo-doi-tuong?idKho=${kho_id}&month=${month}`
    );
  }

  static getTongHopNhapXuatVatTu({ kho_id }: { kho_id: number }): Promise<{
    data: ApiResponse<ChartDataTypeResponseBase>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/kho/chart-data/${kho_id}`
    );
  }

  // ── Company-level reports ────────────────────────────────────────────────

  static isNhapXuatTonKhoPaginated(data: INhapXuatTonKhoResponseBase): boolean {
    return (
      typeof data.last_page === "number" &&
      typeof data.total === "number" &&
      data.last_page > 1
    );
  }

  static getNhapXuatTonKho({
    startMonth,
    endMonth,
    idDanhMucVatTu,
    page,
    search,
    hideZeroActivity,
  }: {
    startMonth: number;
    endMonth: number;
    idDanhMucVatTu: number;
    page?: number;
    search?: string;
    hideZeroActivity?: boolean;
  }): Promise<{ data: ApiResponse<INhapXuatTonKhoResponseBase> }> {
    return apiServiceInstance().vueInstance.axios.get(
      "/api/v1/bao-cao/cong-ty/nhap-xuat-ton-kho",
      {
        params: {
          start_month: startMonth,
          end_month: endMonth,
          id_danh_muc_vat_tu: idDanhMucVatTu,
          ...(page ? { page } : {}),
          ...(search ? { search } : {}),
          ...(hideZeroActivity ? { hide_zero_activity: 1 } : {}),
        },
      }
    );
  }

  static exportNhapXuatTonKho({
    startMonth,
    endMonth,
    idDanhMucVatTu,
    search,
    hideZeroActivity,
    filterLabel,
  }: {
    startMonth: number;
    endMonth: number;
    idDanhMucVatTu: number;
    search?: string;
    hideZeroActivity?: boolean;
    filterLabel?: string;
  }): Promise<{
    data: ApiResponse<ISoCanDoiVatTuExportResponseBase>;
  }> {
    return apiServiceInstance().vueInstance.axios.post(
      "/api/v1/bao-cao/cong-ty/nhap-xuat-ton-kho/export",
      null,
      {
        params: {
          start_month: startMonth,
          end_month: endMonth,
          id_danh_muc_vat_tu: idDanhMucVatTu,
          ...(search ? { search } : {}),
          ...(hideZeroActivity ? { hide_zero_activity: 1 } : {}),
          ...(filterLabel ? { filter_label: filterLabel } : {}),
        },
      }
    );
  }

  static isCompanyReportPaginated(
    pagination?: ICompanyReportPaginationMeta | null
  ): boolean {
    return (
      pagination != null &&
      typeof pagination.last_page === "number" &&
      typeof pagination.total === "number" &&
      pagination.last_page > 1
    );
  }

  static isNhapKhoVatTuPhuTungPaginated(
    data: INhapKhoVatTuPhuTungResponseBase
  ): boolean {
    return BaoCaoService.isCompanyReportPaginated(data.meta.pagination);
  }

  static isGiaThanhSuaChuaCacCapPaginated(
    data: IGiaThanhSuaChuaCacCapResponseBase
  ): boolean {
    return BaoCaoService.isCompanyReportPaginated(data.meta.pagination);
  }

  static getNhapKhoVatTuPhuTung({
    startMonth,
    endMonth,
    idDanhMucVatTu,
    page,
    search,
    hideZeroActivity,
  }: {
    startMonth: number;
    endMonth: number;
    idDanhMucVatTu: number;
    page?: number;
    search?: string;
    hideZeroActivity?: boolean;
  }): Promise<{ data: ApiResponse<INhapKhoVatTuPhuTungResponseBase> }> {
    return apiServiceInstance().vueInstance.axios.get(
      "/api/v1/bao-cao/cong-ty/nhap-kho-phu-tung",
      {
        params: {
          start_month: startMonth,
          end_month: endMonth,
          id_danh_muc_vat_tu: idDanhMucVatTu,
          ...(page ? { page } : {}),
          ...(search ? { search } : {}),
          ...(hideZeroActivity ? { hide_zero_activity: 1 } : {}),
        },
      }
    );
  }

  static exportNhapKhoVatTuPhuTung({
    startMonth,
    endMonth,
    idDanhMucVatTu,
    search,
    hideZeroActivity,
    filterLabel,
  }: {
    startMonth: number;
    endMonth: number;
    idDanhMucVatTu: number;
    search?: string;
    hideZeroActivity?: boolean;
    filterLabel?: string;
  }): Promise<{
    data: ApiResponse<ISoCanDoiVatTuExportResponseBase>;
  }> {
    return apiServiceInstance().vueInstance.axios.post(
      "/api/v1/bao-cao/cong-ty/nhap-kho-phu-tung/export",
      null,
      {
        params: {
          start_month: startMonth,
          end_month: endMonth,
          id_danh_muc_vat_tu: idDanhMucVatTu,
          ...(search ? { search } : {}),
          ...(hideZeroActivity ? { hide_zero_activity: 1 } : {}),
          ...(filterLabel ? { filter_label: filterLabel } : {}),
        },
      }
    );
  }

  static getGiaThanhSuaChuaCacCap({
    startMonth,
    endMonth,
    idCapSuaChua,
    page,
    search,
  }: {
    startMonth: number;
    endMonth: number;
    idCapSuaChua: number;
    page?: number;
    search?: string;
  }): Promise<{ data: ApiResponse<IGiaThanhSuaChuaCacCapResponseBase> }> {
    return apiServiceInstance().vueInstance.axios.get(
      "/api/v1/bao-cao/cong-ty/gia-thanh-sua-chua-cac-cap",
      {
        params: {
          start_month: startMonth,
          end_month: endMonth,
          id_cap_sua_chua: idCapSuaChua,
          ...(page ? { page } : {}),
          ...(search ? { search } : {}),
        },
      }
    );
  }

  static exportGiaThanhSuaChuaCacCap({
    startMonth,
    endMonth,
    idCapSuaChua,
    search,
    filterLabel,
  }: {
    startMonth: number;
    endMonth: number;
    idCapSuaChua: number;
    search?: string;
    filterLabel?: string;
  }): Promise<{
    data: ApiResponse<ISoCanDoiVatTuExportResponseBase>;
  }> {
    return apiServiceInstance().vueInstance.axios.post(
      "/api/v1/bao-cao/cong-ty/gia-thanh-sua-chua-cac-cap/export",
      null,
      {
        params: {
          start_month: startMonth,
          end_month: endMonth,
          id_cap_sua_chua: idCapSuaChua,
          ...(search ? { search } : {}),
          ...(filterLabel ? { filter_label: filterLabel } : {}),
        },
      }
    );
  }

  static searchTaiKhoanTheoHopDong({ s }: { s: string }): Promise<
    ApiResponse<{
      data: ITaiKhoanTheoHopDongResponseBase[];
    }>
  > {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/filters/report/hop-dong?s=${s}`
    );
  }

  static searchToaXeTheoCapSuaChuaReport({
    id_cap_sc,
    id_toa_xe,
    s,
  }: {
    id_cap_sc?: number;
    id_toa_xe?: number;
    s: string;
  }): Promise<
    ApiResponse<{
      data: IToaXeCapSuaChuaReportResponseBase[];
    }>
  > {
    let url = `/api/v1/filters/report/bien-ban-giai-the?s=${s}`;

    if (id_cap_sc !== undefined) {
      url += `&id-cap-sc=${id_cap_sc}`;
    }

    if (id_toa_xe !== undefined) {
      url += `&id-toa-xe=${id_toa_xe}`;
    }

    return apiServiceInstance().vueInstance.axios.get(url);
  }

  static getThuHoiVtptSummary(params: ThuHoiVtptQuery): Promise<{
    data: ThuHoiVtptSummaryResponse;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      "/api/v1/report/thu-hoi-vtpt",
      {
        params: {
          id_kho: params.id_kho,
          from_month: params.from_month,
          to_month: params.to_month,
        },
      }
    );
  }

  static exportThuHoiVtptSummary(params: ThuHoiVtptQuery): Promise<{
    data: ApiResponse<ExcelExportResult>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      "/api/v1/report/thu-hoi-vtpt/export",
      {
        params: {
          id_kho: params.id_kho,
          from_month: params.from_month,
          to_month: params.to_month,
          ...(params.filename ? { filename: params.filename } : {}),
          ...(params.save_file ? { save_file: "true" } : {}),
        },
      }
    );
  }

  static getThuHoiVtptDetail(params: ThuHoiVtptDetailQuery): Promise<{
    data: ThuHoiVtptDetailResponse;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      "/api/v1/report/thu-hoi-vtpt/chi-tiet",
      {
        params: {
          id_kho: params.id_kho,
          from_month: params.from_month,
          to_month: params.to_month,
          id_vat_tu: params.id_vat_tu,
        },
      }
    );
  }

  static exportThuHoiVtptDetail(params: ThuHoiVtptDetailQuery): Promise<{
    data: ApiResponse<ExcelExportResult>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      "/api/v1/report/thu-hoi-vtpt/chi-tiet/export",
      {
        params: {
          id_kho: params.id_kho,
          from_month: params.from_month,
          to_month: params.to_month,
          id_vat_tu: params.id_vat_tu,
          ...(params.filename ? { filename: params.filename } : {}),
          ...(params.save_file ? { save_file: "true" } : {}),
        },
      }
    );
  }

  static fetchThuHoiVtptMaterialOptions({
    id_kho,
    from_month,
    to_month,
    s,
  }: {
    id_kho: number;
    from_month: number;
    to_month: number;
    s?: string;
  }): Promise<{
    data: ApiResponse<ThuHoiVatTuPickerItem[]>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      "/api/v1/filters/report/thu-hoi-vtpt",
      {
        params: {
          id_kho,
          from_month,
          to_month,
          ...(s ? { s } : {}),
        },
      }
    );
  }
}
